import { useContext, useEffect } from 'react';
import { MantineProvider } from '@mantine/core';
import { useRouter } from 'next/router';
import Cookies from 'js-cookie';

import { lightTheme } from '~/config/theme';
import { UserContext } from '~/components/providers/UserProvider';
import AuthModalsContextProvider from '~/domains/auth/context/AuthModalsContext/AuthModalsContextProvider';
import SignUpSteps from '~/domains/auth/components/SignUpSteps';
import SignUpSlideshow from '~/domains/auth/components/SignUpSlideshow';
import { useEORedirect } from '~/domains/auth/hooks/useEORedirect';
import useRedirectAfterAuth from '~/domains/auth/hooks/useRedirectAfterAuth';
import { SplashLoader } from '~/domains/common/components/SplashLoader/SplashLoader';

import useStyles from './SignUp.styles';

function SignUp() {
  const { classes } = useStyles();
  const router = useRouter();
  const { isSignUpInitialised, user } = useContext(UserContext);
  const handleRedirectAfterAuth = useRedirectAfterAuth();
  const { shouldRedirect: shouldRedirectToEOSite, eoRedirectUrl } =
    useEORedirect(!isSignUpInitialised);

  const redirectTo = router.query.redirectTo as string;
  const accessToken = Cookies.get('accessToken');

  const isAlreadyLoggedIn =
    (!!user || (!!accessToken && shouldRedirectToEOSite)) && !isSignUpInitialised;

  // User Already logged in. Select appropriate redirect URL
  useEffect(() => {
    // Prevent redirects after login has been initialised (it has it's own redirect handler)
    if (isSignUpInitialised) return;

    // Using access token specifically, because EO login doesn't set the user in the context or local storage
    if (accessToken && shouldRedirectToEOSite && eoRedirectUrl) {
      handleRedirectAfterAuth(eoRedirectUrl, true);
    }

    // We need two separate checks, because we might have to wait for the redirectURL (waiting on SUT)
    if (user && !shouldRedirectToEOSite) {
      handleRedirectAfterAuth(redirectTo);
    }
  }, [accessToken, eoRedirectUrl, isSignUpInitialised, redirectTo, shouldRedirectToEOSite, user]);

  return (
    <MantineProvider theme={lightTheme}>
      {isAlreadyLoggedIn && <SplashLoader />}
      <AuthModalsContextProvider>
        <div
          className={classes.signUpContainer}
          style={isAlreadyLoggedIn ? { display: 'none' } : undefined}
        >
          <SignUpSteps />
          <SignUpSlideshow />
        </div>
      </AuthModalsContextProvider>
    </MantineProvider>
  );
}

export default SignUp;
